import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 10;

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('pending');
  const [details, setDetails] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      navigate('/dashboard', { replace: true });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        setDetails(res.data);
        setStatus(res.data.payment_status);
        if (res.data.payment_status === 'paid') {
          await refreshUser();
          return; // done
        }
        if (res.data.payment_status === 'expired' || res.data.payment_status === 'failed') {
          return;
        }
      } catch (e) {
        // transient
      }
      attemptsRef.current += 1;
      if (attemptsRef.current < MAX_ATTEMPTS) {
        setTimeout(poll, POLL_INTERVAL_MS);
      } else {
        setStatus('timeout');
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-lg p-6 shadow-sm text-center" data-testid="payment-success-page">
        {status === 'paid' ? (
          <>
            <div className="flex items-center justify-center mb-3">
              <div className="bg-emerald-100 p-3 rounded-full">
                <CheckCircle2 size={40} className="text-emerald-600" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">¡Suscripción activada!</h1>
            {details && (
              <div className="text-sm text-slate-600 space-y-1 mb-6">
                <p>Plan: <strong>{details.plan_key}</strong></p>
                <p>Importe: <strong>€{(details.amount_cents / 100).toFixed(2)}</strong></p>
                {details.period_end && (
                  <p>Acceso hasta: <strong>{new Date(details.period_end).toLocaleDateString('es-ES', {day:'2-digit',month:'long',year:'numeric'})}</strong></p>
                )}
              </div>
            )}
            <Button
              onClick={() => navigate('/dashboard')}
              data-testid="go-dashboard-button"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Ir al panel
            </Button>
          </>
        ) : status === 'timeout' ? (
          <>
            <div className="flex items-center justify-center mb-3">
              <div className="bg-amber-100 p-3 rounded-full">
                <XCircle size={40} className="text-amber-600" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Estamos verificando el pago…</h1>
            <p className="text-sm text-slate-600 mb-6">Puede tardar unos segundos en llegarnos la confirmación. Revísalo en tu perfil en un momento.</p>
            <Button onClick={() => navigate('/dashboard')} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
              Ir al panel
            </Button>
          </>
        ) : status === 'failed' || status === 'expired' ? (
          <>
            <div className="flex items-center justify-center mb-3">
              <div className="bg-red-100 p-3 rounded-full">
                <XCircle size={40} className="text-red-600" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Pago no completado</h1>
            <p className="text-sm text-slate-600 mb-6">La sesión de pago ha caducado o ha fallado. Inténtalo de nuevo.</p>
            <Button onClick={() => navigate('/dashboard')} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
              Volver
            </Button>
          </>
        ) : (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
            <p className="text-slate-600">Confirmando el pago…</p>
          </>
        )}
      </div>
    </div>
  );
};

export const PaymentCancel = () => {
  const navigate = useNavigate();
  useEffect(() => {
    toast('Pago cancelado');
  }, []);
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-lg p-6 shadow-sm text-center" data-testid="payment-cancel-page">
        <div className="flex items-center justify-center mb-3">
          <div className="bg-slate-100 p-3 rounded-full">
            <XCircle size={40} className="text-slate-500" />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Pago cancelado</h1>
        <p className="text-sm text-slate-600 mb-6">No se ha cobrado nada. Puedes volver a intentarlo cuando quieras.</p>
        <Button onClick={() => navigate('/dashboard')} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
          Volver al panel
        </Button>
      </div>
    </div>
  );
};
